import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X, CreditCard, Loader2, Clock } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionBanner() {
  const { user, token } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptionStatus(response.data);
      } catch (error) {
        console.error("Error fetching subscription status:", error);
      }
    };

    fetchSubscriptionStatus();
    const interval = setInterval(fetchSubscriptionStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  const handleUpgradeClick = () => {
    if (location.pathname === "/abbonamento") {
      const plansSection = document.querySelector('[data-testid="available-plans"]');
      if (plansSection) {
        plansSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate("/abbonamento");
    }
  };

  const handlePayNow = async () => {
    // Direct payment for current plan when trial expired
    setProcessingPayment(true);
    try {
      const response = await axios.post(
        `${API}/api/stripe/create-checkout`,
        {
          plan_key: subscriptionStatus.piano || "base",
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      navigate("/abbonamento");
    } finally {
      setProcessingPayment(false);
    }
  };

  // Don't show banner if user is not titolare or dismissed
  if (!subscriptionStatus || dismissed || user?.ruolo !== "titolare") {
    return null;
  }

  const { 
    is_trial, 
    trial_days_remaining, 
    trial_expired,
    payment_completed,
    over_limit, 
    current_employees, 
    max_employees, 
    grace_days_remaining, 
    is_blocked, 
    piano_nome, 
    next_plan,
    tier_price
  } = subscriptionStatus;

  // Trial banner (not expired yet)
  const pricingV2 = subscriptionStatus.pricing_model === "v2";

  // Modello V2: banner solo se superata la soglia Free senza abbonamento attivo
  if (pricingV2) {
    if (over_limit && !payment_completed) {
      return (
        <div className={`fixed bottom-0 left-0 right-0 z-50 p-4 ${is_blocked ? 'bg-red-600' : 'bg-amber-500'} text-white shadow-lg lg:ml-64`} data-testid="v2-overlimit-banner">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center gap-3 justify-between">
            <p className="text-sm font-medium text-center sm:text-left">
              {is_blocked
                ? `⛔ Piano Free superato (${current_employees} dipendenti) e periodo di tolleranza terminato: attiva il piano per continuare.`
                : `⚠️ Hai superato i 5 dipendenti del piano Free (${current_employees}). Ti restano ${grace_days_remaining} giorni per attivare il piano.`}
            </p>
            <Button size="sm" className="bg-white text-slate-900 hover:bg-slate-100 shrink-0" onClick={() => navigate("/abbonamento")} data-testid="v2-banner-cta">
              Attiva ora
            </Button>
          </div>
        </div>
      );
    }
    return null;
  }

  if (is_trial && !trial_expired && !payment_completed) {
    return (
      <div className="fixed bottom-4 right-4 z-50 max-w-sm lg:max-w-md">
        <div className="bg-blue-500 text-white shadow-lg rounded-lg p-3">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">
                🎉 Prova gratuita - {trial_days_remaining} giorni
              </p>
              <p className="text-xs opacity-90 mt-0.5">
                Acquista per continuare dopo il trial
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Button
                  size="sm"
                  onClick={handleUpgradeClick}
                  className="gap-1 bg-white text-blue-600 hover:bg-blue-50 text-xs h-7 px-2"
                >
                  <CreditCard className="h-3 w-3" />
                  Acquista
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDismissed(true)}
                  className="text-white hover:bg-white/20 text-xs h-7 px-2"
                >
                  Chiudi
                </Button>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDismissed(true)}
              className="text-white hover:bg-white/20 h-6 w-6 shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Trial expired banner
  if (trial_expired && !payment_completed) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-red-600 text-white shadow-lg lg:ml-64">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 shrink-0" />
            <div>
              <p className="font-semibold">
                ⛔ Prova gratuita terminata
              </p>
              <p className="text-sm opacity-90">
                Effettua il pagamento per riattivare tutte le funzionalità dell'app.
              </p>
            </div>
          </div>
          
          <Button
            onClick={handlePayNow}
            disabled={processingPayment}
            className="gap-2 bg-white text-red-600 hover:bg-red-50"
          >
            {processingPayment ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CreditCard className="h-4 w-4" />
            )}
            Paga ora
          </Button>
        </div>
      </div>
    );
  }

  // Over limit banner (only if payment completed)
  if (over_limit && payment_completed) {
    return (
      <div className={`fixed bottom-0 left-0 right-0 z-50 p-4 ${is_blocked ? 'bg-red-600' : 'bg-amber-500'} text-white shadow-lg lg:ml-64`}>
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 shrink-0" />
            <div>
              {is_blocked ? (
                <p className="font-semibold">
                  ⛔ Registrazioni bloccate! Hai {current_employees} dipendenti ma il piano "{piano_nome}" ne consente massimo {max_employees}.
                </p>
              ) : (
                <p className="font-semibold">
                  ⚠️ Hai {current_employees} dipendenti ma il piano "{piano_nome}" ne consente massimo {max_employees}. 
                  <span className="ml-2 underline">Effettua l'upgrade entro {grace_days_remaining} giorni.</span>
                </p>
              )}
              {next_plan && (
                <p className="text-sm opacity-90 mt-1">
                  Piano consigliato: {next_plan.name} (fino a {next_plan.max_employees} dipendenti){tier_price ? ` — mensile attuale €${Number(tier_price).toFixed(2).replace('.', ',')}/mese in base ai dipendenti` : ''}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={handleUpgradeClick}
              className={`gap-2 ${is_blocked ? 'bg-white text-red-600 hover:bg-red-50' : 'bg-white text-amber-600 hover:bg-amber-50'}`}
            >
              <CreditCard className="h-4 w-4" />
              Upgrade
            </Button>
            
            {!is_blocked && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDismissed(true)}
                className="text-white hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
